/**
 * Enhanced Export Service - F068 Continuous Export
 * Extends basic exportService with organized folder structure,
 * auto-export, scheduled backups, and attachment handling.
 *
 * Related: ADR-002 Hybrid Architecture Strategy
 */

import { dataRepository } from '../repository/DataRepository';
import { supabase } from '../lib/supabase';
import { getUserId, restSelect, restInsert, restUpdate } from '../lib/supabaseRest';
import type { PKMObject, Block } from '../models/types';
import { objectToMarkdown } from './exportService';
import JSZip from 'jszip';

// ============================================
// Types
// ============================================

export interface ExportOptions {
  organization: 'type' | 'collection' | 'tag' | 'flat' | 'hybrid';
  includeAttachments: boolean;
  includeFrontmatter: boolean;
  frontmatterFields?: string[];
}

export interface ExportProgress {
  status: 'starting' | 'exporting' | 'attachments' | 'zipping' | 'downloading' | 'complete' | 'error';
  current: number;
  total: number;
  message: string;
  error?: string;
}

export interface ExportSettings {
  auto_export_enabled: boolean;
  scheduled_backup_enabled: boolean;
  export_attachments: boolean;
  folder_organization: 'type' | 'collection' | 'tag' | 'flat' | 'hybrid';
  backup_frequency: 'hourly' | 'daily' | 'weekly' | 'manual';
  backup_destination?: 'supabase' | 'drive' | 'both'; // NEW: Where to upload backups
  debounce_ms: number;
  include_frontmatter: boolean;
  frontmatter_properties: string[];
}

export interface ExportJob {
  id: string;
  user_id: string;
  job_type: 'auto' | 'manual' | 'scheduled';
  status: 'pending' | 'running' | 'completed' | 'failed';
  objects_count: number;
  attachments_count: number;
  files_size_bytes: number;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  error_message?: string;
  created_at: string;
}

// ============================================
// Export Settings Management
// ============================================

/**
 * Load export settings for current user
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
export async function loadExportSettings(): Promise<ExportSettings> {
  try {
    const userId = getUserId();
    if (!userId) {
      console.log('[ExportService] No user found, returning defaults');
      return getDefaultSettings();
    }

    const { data, error } = await restSelect<ExportSettings>('user_export_settings', {
      select: '*',
      eq: { user_id: userId },
      limit: 1
    });

    if (error) {
      console.error('[ExportService] Failed to load settings:', error);
      console.log('[ExportService] Falling back to defaults');
      return getDefaultSettings();
    }

    // Return settings or defaults if no row exists
    if (!data || data.length === 0) {
      console.log('[ExportService] No settings found, returning defaults');
      return getDefaultSettings();
    }

    console.log('[ExportService] Settings loaded successfully');
    return data[0] as ExportSettings;
  } catch (error) {
    console.error('[ExportService] Error loading settings:', error);
    console.log('[ExportService] Falling back to defaults');
    return getDefaultSettings();
  }
}

/**
 * Save export settings for current user
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
export async function saveExportSettings(settings: Partial<ExportSettings>): Promise<boolean> {
  try {
    console.log('[ExportService] Saving settings:', settings);

    const userId = getUserId();
    if (!userId) {
      console.error('[ExportService] No user found');
      return false;
    }

    console.log('[ExportService] User ID:', userId);

    // Prepare the upsert payload
    const payload = {
      user_id: userId,
      ...settings,
      updated_at: new Date().toISOString()
    };

    console.log('[ExportService] Upserting payload:', payload);

    const { data, error } = await restInsert('user_export_settings', payload, {
      upsert: true,
      onConflict: 'user_id',
      returning: true
    });

    if (error) {
      console.error('[ExportService] Failed to save settings:', error);
      console.error('[ExportService] Error details:', JSON.stringify(error, null, 2));
      return false;
    }

    console.log('[ExportService] Settings saved successfully:', data);
    return true;
  } catch (error) {
    console.error('[ExportService] Error saving settings:', error);
    return false;
  }
}

/**
 * Get default export settings
 */
export function getDefaultSettings(): ExportSettings {
  return {
    auto_export_enabled: false,
    scheduled_backup_enabled: false,
    export_attachments: true,
    folder_organization: 'type',
    backup_frequency: 'daily',
    backup_destination: 'supabase', // Default to Supabase Storage
    debounce_ms: 3000,
    include_frontmatter: true,
    frontmatter_properties: ['id', 'type', 'status', 'tags', 'created', 'updated']
  };
}

// ============================================
// Core Export Functions
// ============================================

/**
 * Export all objects to organized ZIP file
 */
export async function exportToZipOrganized(
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<Blob> {
  const zip = new JSZip();
  let objectsExported = 0;
  let attachmentsExported = 0;
  let totalSize = 0;

  try {
    // Create export job
    const job = await createExportJob('manual');
    if (!job) throw new Error('Failed to create export job');

    // Update progress
    onProgress?.({
      status: 'starting',
      current: 0,
      total: 0,
      message: 'Loading objects...'
    });

    // Get all non-deleted objects
    const allObjects = await dataRepository.getAllObjects();
    const objects = allObjects.filter(obj => !obj.deletedAt);
    const total = objects.length;

    console.log(`[ExportService] Found ${allObjects.length} total objects, ${objects.length} non-deleted`);

    if (total === 0) {
      console.warn('[ExportService] No objects to export!');
      throw new Error('No objects found to export. Create some notes, tasks, or projects first.');
    }

    console.log(`[ExportService] Exporting ${total} objects`);

    // Update job status
    await updateExportJob(job.id, {
      status: 'running',
      started_at: new Date().toISOString()
    });

    // Export each object
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];

      try {
        console.log(`[ExportService] Exporting object ${i + 1}/${total}: ${obj.name || 'Untitled'} (${obj.id})`);

        // Convert to markdown
        const markdown = await objectToMarkdown(obj.id, {
          includeProperties: true,
          includeBlocks: true,
          includeTags: true,
          includeRelationships: true
        });

        console.log(`[ExportService] Markdown length: ${markdown.length} bytes`);

        if (!markdown || markdown.length === 0) {
          console.warn(`[ExportService] Empty markdown for object ${obj.id}, skipping`);
          continue;
        }

        // Determine folder path based on organization strategy
        const folderPath = getFolderPath(obj, options.organization);
        const fileName = sanitizeFileName(obj.name || 'untitled') + '.md';
        const filePath = folderPath ? `${folderPath}/${fileName}` : fileName;

        console.log(`[ExportService] Adding to ZIP: ${filePath}`);

        // Add to ZIP
        zip.file(filePath, markdown);
        totalSize += markdown.length;
        objectsExported++;

        // Export attachments if enabled
        if (options.includeAttachments) {
          const attachmentCount = await exportAttachments(zip, obj, folderPath);
          attachmentsExported += attachmentCount;
        }

        // Update progress
        onProgress?.({
          status: 'exporting',
          current: i + 1,
          total,
          message: `Exported ${i + 1}/${total}: ${obj.name || 'Untitled'}`
        });

      } catch (error) {
        console.error(`[ExportService] Failed to export object ${obj.id}:`, error);
        console.error(`[ExportService] Error details:`, error);
        // Continue with other objects
      }
    }

    console.log(`[ExportService] Export loop complete. Objects exported: ${objectsExported}, Total size: ${totalSize}`);

    // Generate ZIP
    onProgress?.({
      status: 'zipping',
      current: total,
      total,
      message: 'Creating archive...'
    });

    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });

    totalSize += blob.size;

    // Update job completion
    await updateExportJob(job.id, {
      status: 'completed',
      objects_count: objectsExported,
      attachments_count: attachmentsExported,
      files_size_bytes: totalSize,
      completed_at: new Date().toISOString(),
      duration_ms: Date.now() - new Date(job.created_at).getTime()
    });

    // Success progress
    onProgress?.({
      status: 'complete',
      current: total,
      total,
      message: `Export complete! ${objectsExported} objects, ${attachmentsExported} attachments`
    });

    console.log(`[ExportService] Export complete: ${objectsExported} objects, ${attachmentsExported} attachments, ${formatBytes(totalSize)}`);

    return blob;

  } catch (error: any) {
    console.error('[ExportService] Export failed:', error);

    // Update job error
    const jobs = await getRecentExportJobs(1);
    if (jobs.length > 0) {
      await updateExportJob(jobs[0].id, {
        status: 'failed',
        error_message: error.message,
        completed_at: new Date().toISOString()
      });
    }

    // Error progress
    onProgress?.({
      status: 'error',
      current: 0,
      total: 0,
      message: `Export failed: ${error.message}`,
      error: error.message
    });

    throw error;
  }
}

/**
 * Download export as ZIP file
 */
export async function downloadExport(
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  onProgress?.({
    status: 'starting',
    current: 0,
    total: 0,
    message: 'Starting export...'
  });

  const blob = await exportToZipOrganized(options, onProgress);

  // Download ZIP
  onProgress?.({
    status: 'downloading',
    current: 1,
    total: 1,
    message: 'Downloading...'
  });

  const timestamp = new Date().toISOString().split('T')[0];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pkm-export-${timestamp}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log(`[ExportService] Downloaded pkm-export-${timestamp}.zip`);
}

// ============================================
// Folder Organization
// ============================================

/**
 * Get folder path based on organization strategy
 */
function getFolderPath(obj: PKMObject, strategy: string): string {
  switch (strategy) {
    case 'type':
      // Organize by object type: notes/, tasks/, projects/
      return obj.type || 'misc';

    case 'collection':
      // Use first collection or 'uncategorized'
      // TODO: Load collections from database when collection feature implemented
      return 'uncategorized';

    case 'tag':
      // Use first tag or 'untagged'
      // Tags might be stored in properties
      return 'untagged';

    case 'hybrid':
      // type/collection structure
      return `${obj.type || 'misc'}/uncategorized`;

    case 'flat':
    default:
      // All files in root
      return '';
  }
}

/**
 * Sanitize filename for filesystem
 */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid chars
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Collapse multiple underscores
    .substring(0, 100) // Limit length
    .replace(/^_+|_+$/g, ''); // Trim underscores from ends
}

// ============================================
// Attachment Export
// ============================================

/**
 * Export attachments (images, files) for an object
 * Returns number of attachments exported
 */
async function exportAttachments(
  zip: JSZip,
  obj: PKMObject,
  folderPath: string
): Promise<number> {
  let count = 0;

  try {
    // Get all blocks for this object
    const blocks = await dataRepository.getBlocksForObject(obj.id);

    // Find attachment blocks (image, file, audio, video, drawing)
    const attachmentBlocks = blocks.filter(b =>
      ['image', 'file', 'audio', 'video', 'drawing'].includes(b.type)
    );

    if (attachmentBlocks.length === 0) return 0;

    for (const block of attachmentBlocks) {
      const metadata = block.metadata || {};
      const filePath = metadata.imagePath || metadata.filePath || metadata.audioPath || metadata.drawingPath;

      if (!filePath) continue;

      try {
        // Download from Supabase Storage
        const { data, error } = await supabase.storage
          .from('attachments')
          .download(filePath);

        if (error) {
          console.warn(`[ExportService] Failed to download attachment ${filePath}:`, error);
          continue;
        }

        // Add to ZIP in attachments subfolder
        const fileName = filePath.split('/').pop() || 'attachment';
        const attachmentPath = folderPath
          ? `${folderPath}/attachments/${fileName}`
          : `attachments/${fileName}`;

        zip.file(attachmentPath, data);
        count++;

      } catch (error) {
        console.warn(`[ExportService] Error exporting attachment ${filePath}:`, error);
      }
    }

  } catch (error) {
    console.error(`[ExportService] Error loading blocks for object ${obj.id}:`, error);
  }

  return count;
}

// ============================================
// Export Job Management
// ============================================

/**
 * Create new export job
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
async function createExportJob(jobType: 'auto' | 'manual' | 'scheduled'): Promise<ExportJob | null> {
  try {
    const userId = getUserId();
    if (!userId) return null;

    const { data, error } = await restInsert<ExportJob>('export_jobs', {
      user_id: userId,
      job_type: jobType,
      status: 'pending'
    }, {
      returning: true
    });

    if (error) {
      console.error('[ExportService] Failed to create job:', error);
      return null;
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('[ExportService] Error creating job:', error);
    return null;
  }
}

/**
 * Update export job
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
async function updateExportJob(
  jobId: string,
  updates: Partial<ExportJob>
): Promise<boolean> {
  try {
    const { error } = await restUpdate('export_jobs', updates, {
      eq: { id: jobId }
    });

    if (error) {
      console.error('[ExportService] Failed to update job:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[ExportService] Error updating job:', error);
    return false;
  }
}

/**
 * Get recent export jobs
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
export async function getRecentExportJobs(limit: number = 10): Promise<ExportJob[]> {
  try {
    const userId = getUserId();
    if (!userId) return [];

    const { data, error } = await restSelect<ExportJob>('export_jobs', {
      select: '*',
      eq: { user_id: userId },
      order: 'created_at.desc',
      limit
    });

    if (error) {
      console.error('[ExportService] Failed to load jobs:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[ExportService] Error loading jobs:', error);
    return [];
  }
}

// ============================================
// Supabase Storage Backups
// ============================================

export interface BackupInfo {
  id: string;
  user_id: string;
  backup_name: string;
  storage_path: string;
  file_size: number;
  object_count: number;
  block_count: number;
  created_at: string;
}

/**
 * Upload backup to Supabase Storage
 * Returns backup info on success
 */
export async function uploadBackupToStorage(
  blob: Blob,
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<BackupInfo | null> {
  try {
    onProgress?.({
      status: 'starting',
      current: 0,
      total: 100,
      message: 'Uploading backup to cloud storage...'
    });

    const userId = getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Generate backup name with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const backupName = `backup-${timestamp}.zip`;
    const storagePath = `${userId}/${backupName}`;

    // Upload to Supabase Storage
    onProgress?.({
      status: 'exporting',
      current: 50,
      total: 100,
      message: 'Uploading...'
    });

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('user-backups')
      .upload(storagePath, blob, {
        contentType: 'application/zip',
        upsert: false // Don't overwrite, create new file
      });

    if (uploadError) {
      console.error('[ExportService] Upload failed:', uploadError);
      throw uploadError;
    }

    // Get object count and block count for metadata
    const objects = await dataRepository.getAllObjects();
    let blockCount = 0;
    for (const obj of objects) {
      const blocks = await dataRepository.getBlocksForObject(obj.id);
      blockCount += blocks.length;
    }

    // Save backup metadata to database
    const { data: historyData, error: historyError } = await restInsert<BackupInfo>('backup_history', {
      user_id: userId,
      backup_name: backupName,
      storage_path: storagePath,
      file_size: blob.size,
      object_count: objects.length,
      block_count: blockCount
    }, {
      returning: true
    });

    if (historyError) {
      console.error('[ExportService] Failed to save backup history:', historyError);
      // Don't throw - backup is uploaded, history is just metadata
    }

    const backupInfo = historyData && historyData.length > 0 ? historyData[0] : null;

    // Clean up old backups (keep last 7)
    onProgress?.({
      status: 'exporting',
      current: 90,
      total: 100,
      message: 'Cleaning up old backups...'
    });

    await cleanupOldBackups(7);

    onProgress?.({
      status: 'complete',
      current: 100,
      total: 100,
      message: `Backup saved to cloud storage: ${formatBytes(blob.size)}`
    });

    console.log(`[ExportService] Backup uploaded successfully: ${storagePath}`);

    return backupInfo;

  } catch (error: any) {
    console.error('[ExportService] Backup upload failed:', error);
    onProgress?.({
      status: 'error',
      current: 0,
      total: 100,
      message: `Upload failed: ${error.message}`,
      error: error.message
    });
    return null;
  }
}

// ============================================
// Google Drive Upload Functions
// ============================================

/**
 * Upload backup to Google Drive
 * Returns backup info on success
 */
export async function uploadBackupToDrive(
  blob: Blob,
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<BackupInfo | null> {
  try {
    const { googleDriveService } = await import('./googleDriveService');

    onProgress?.({
      status: 'starting',
      current: 0,
      total: 100,
      message: 'Uploading backup to Google Drive...'
    });

    // Check connection
    const status = await googleDriveService.getConnectionStatus();
    if (!status.connected) {
      throw new Error('Not connected to Google Drive');
    }

    const userId = getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Generate backup name with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const backupName = `backup-${timestamp}.zip`;

    // Upload to Drive
    onProgress?.({
      status: 'exporting',
      current: 50,
      total: 100,
      message: 'Uploading to Drive...'
    });

    const result = await googleDriveService.uploadBackup(blob, backupName);

    if (!result.success || !result.file) {
      throw new Error(result.error || 'Upload failed');
    }

    // Get object count and block count for metadata
    const objects = await dataRepository.getAllObjects();
    let blockCount = 0;
    for (const obj of objects) {
      const blocks = await dataRepository.getBlocksForObject(obj.id);
      blockCount += blocks.length;
    }

    // Save backup metadata to database
    const { data: historyData, error: historyError } = await restInsert<BackupInfo>('backup_history', {
      user_id: userId,
      backup_name: backupName,
      storage_path: result.file.id, // Store Drive file ID
      file_size: blob.size,
      object_count: objects.length,
      block_count: blockCount,
      backup_destination: 'drive',
      drive_file_id: result.file.id,
      drive_folder_id: result.file.folderId
    }, {
      returning: true
    });

    if (historyError) {
      console.error('[ExportService] Failed to save backup history:', historyError);
      // Don't throw - backup is uploaded, history is just metadata
    }

    const backupInfo = historyData && historyData.length > 0 ? historyData[0] : null;

    onProgress?.({
      status: 'complete',
      current: 100,
      total: 100,
      message: `Backup saved to Google Drive: ${formatBytes(blob.size)}`
    });

    console.log(`[ExportService] Backup uploaded to Drive successfully: ${result.file.id}`);

    return backupInfo;

  } catch (error: any) {
    console.error('[ExportService] Drive upload failed:', error);
    onProgress?.({
      status: 'error',
      current: 0,
      total: 100,
      message: `Drive upload failed: ${error.message}`,
      error: error.message
    });
    return null;
  }
}

/**
 * Upload backup to both Supabase Storage and Google Drive
 * Returns both backup infos on success
 */
export async function uploadBackupToBoth(
  blob: Blob,
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<{ supabase: BackupInfo | null; drive: BackupInfo | null }> {
  try {
    onProgress?.({
      status: 'starting',
      current: 0,
      total: 100,
      message: 'Uploading backup to Supabase and Google Drive...'
    });

    // Upload to Supabase first
    onProgress?.({
      status: 'exporting',
      current: 25,
      total: 100,
      message: 'Uploading to Supabase Storage...'
    });

    const supabaseResult = await uploadBackupToStorage(blob, options, (progress) => {
      // Scale progress to 0-50%
      if (progress.status !== 'error') {
        onProgress?.({
          ...progress,
          current: Math.floor(progress.current * 0.5),
          total: 100,
          message: `Supabase: ${progress.message}`
        });
      }
    });

    // Upload to Drive
    onProgress?.({
      status: 'exporting',
      current: 50,
      total: 100,
      message: 'Uploading to Google Drive...'
    });

    const driveResult = await uploadBackupToDrive(blob, options, (progress) => {
      // Scale progress to 50-100%
      if (progress.status !== 'error') {
        onProgress?.({
          ...progress,
          current: 50 + Math.floor(progress.current * 0.5),
          total: 100,
          message: `Drive: ${progress.message}`
        });
      }
    });

    onProgress?.({
      status: 'complete',
      current: 100,
      total: 100,
      message: `Backup saved to both locations: ${formatBytes(blob.size)}`
    });

    return {
      supabase: supabaseResult,
      drive: driveResult
    };

  } catch (error: any) {
    console.error('[ExportService] Multi-upload failed:', error);
    onProgress?.({
      status: 'error',
      current: 0,
      total: 100,
      message: `Upload failed: ${error.message}`,
      error: error.message
    });
    return {
      supabase: null,
      drive: null
    };
  }
}

/**
 * Get list of user's backups from storage
 * FIXED: Uses REST API instead of Supabase client to avoid hanging
 */
export async function getBackupList(): Promise<BackupInfo[]> {
  try {
    const userId = getUserId();
    if (!userId) return [];

    const { data, error } = await restSelect<BackupInfo>('backup_history', {
      select: '*',
      eq: { user_id: userId },
      order: 'created_at.desc'
    });

    if (error) {
      console.error('[ExportService] Failed to load backup list:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('[ExportService] Error loading backup list:', error);
    return [];
  }
}

/**
 * Download backup from Supabase Storage
 */
export async function downloadBackupFromStorage(storagePath: string): Promise<void> {
  try {
    const { data, error } = await supabase.storage
      .from('user-backups')
      .download(storagePath);

    if (error) {
      console.error('[ExportService] Download failed:', error);
      throw error;
    }

    // Trigger browser download
    const fileName = storagePath.split('/').pop() || 'backup.zip';
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`[ExportService] Backup downloaded: ${fileName}`);
  } catch (error) {
    console.error('[ExportService] Error downloading backup:', error);
    throw error;
  }
}

/**
 * Delete backup from storage
 */
export async function deleteBackup(backupId: string, storagePath: string): Promise<boolean> {
  try {
    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('user-backups')
      .remove([storagePath]);

    if (storageError) {
      console.error('[ExportService] Failed to delete from storage:', storageError);
      // Continue anyway to remove from database
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('backup_history')
      .delete()
      .eq('id', backupId);

    if (dbError) {
      console.error('[ExportService] Failed to delete backup history:', dbError);
      return false;
    }

    console.log(`[ExportService] Backup deleted: ${backupId}`);
    return true;
  } catch (error) {
    console.error('[ExportService] Error deleting backup:', error);
    return false;
  }
}

/**
 * Clean up old backups (keep last N)
 * FIXED: Uses getUserId() instead of Supabase client to avoid hanging
 */
async function cleanupOldBackups(keepCount: number = 7): Promise<void> {
  try {
    const userId = getUserId();
    if (!userId) return;

    // Call database function to clean up old backups
    const { error } = await supabase.rpc('cleanup_old_backups', {
      p_user_id: userId,
      p_keep_count: keepCount
    });

    if (error) {
      console.error('[ExportService] Cleanup failed:', error);
    } else {
      console.log(`[ExportService] Old backups cleaned up (keeping last ${keepCount})`);
    }
  } catch (error) {
    console.error('[ExportService] Error during cleanup:', error);
  }
}

// ============================================
// Utilities
// ============================================

/**
 * Format bytes to human-readable size
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
